"use client";

import type { ReactNode, RefObject } from "react";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  CalendarX2,
  Link2,
  MessageCircle,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { cn } from "@/lib/utils";

import {
  PessoasScreen,
  EscalasScreen,
  FinancasScreen,
} from "./features-previews/hero-screens";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { SectionHeading } from "./primitives";
import {
  buttonClass,
  CONTAINER,
  SECTION_Y,
  scrollToSection,
} from "./ui-tokens";

/**
 * Seção de recursos da landing.
 *
 * Três blocos grandes, um por dor que decide a compra. O título é a dor
 * resolvida, não o nome do módulo — pastor não procura "a área de
 * Crescimento", ele procura "perco visitante". Depois, uma linha declara os
 * outros módulos e manda para /features, que é onde o catálogo mora.
 *
 * O bloco de escalas carrega o MECANISMO (a antiga seção mechanism.tsx): as
 * três regras que explicam por que a escala sai em 15 minutos. Antes era uma
 * seção inteira antes desta, e a pessoa lia a regra longe da tela que a
 * aplica. As regras são as que o produto realmente aplica (ver
 * /escalas/disponibilidade e /escalas/preferencias, links públicos sem login)
 * — não invente restrição aqui sem que o gerador de escalas a respeite.
 *
 * O que ficou de fora e por quê: abas (escondiam módulos e faziam o scroll
 * pular), um preview por módulo (treze linguagens visuais diferentes) e a
 * grade com os vinte ícones, que ocupava quase uma tela para entregar vinte
 * rótulos que ninguém lê em sequência.
 */

/* A home não conta os módulos um por um: o número exato muda a cada módulo
   novo, e a promessa é "mais de 20" (são 23 páginas em features-data.ts). */

const rules = [
  {
    icon: Link2,
    label: "O voluntário responde por link",
    desc: "Sem app e sem senha. Ele abre no celular e marca os dias do mês em que não pode servir.",
  },
  {
    icon: CalendarCheck,
    label: "E diz qual culto prefere",
    desc: "Quem serve melhor no domingo de manhã não é escalado à noite por acaso.",
  },
  {
    icon: ShieldCheck,
    label: "Com limite de escalas por pessoa",
    desc: "É o que impede os mesmos três de sempre de carregarem a equipe até desistirem.",
  },
];

/** A regra das escalas, dentro do bloco 02. Tem goal próprio de seção vista
 *  (`mechanism`) para o funil continuar comparável com o de antes. */
function EscalasMechanism() {
  const ref = useSectionViewGoal("mechanism");

  return (
    <div ref={ref as RefObject<HTMLDivElement>} className="mt-8">
      <p className="text-sm font-semibold text-foreground">
        Escala não se monta perguntando quem pode. Se monta sabendo quem não
        pode:
      </p>
      <ul className="mt-4 flex flex-col gap-4">
        {rules.map((rule) => (
          <li key={rule.label} className="flex gap-3">
            <rule.icon
              className="mt-0.5 size-4 shrink-0 text-primary"
              strokeWidth={2}
              aria-hidden="true"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">{rule.label}.</span>{" "}
              {rule.desc}
            </p>
          </li>
        ))}
      </ul>

      <dl className="mt-6 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-secondary/60 px-4 py-3">
          <dt className="text-xs text-muted-foreground">Antes</dt>
          <dd className="mt-0.5 text-xl font-semibold tracking-tight text-muted-foreground tabular-nums">
            3 a 4 horas
          </dd>
        </div>
        <div className="rounded-xl bg-primary/8 px-4 py-3">
          <dt className="text-xs text-muted-foreground">Com o Ecclesiato</dt>
          <dd className="mt-0.5 text-xl font-semibold tracking-tight text-foreground tabular-nums">
            15 minutos
          </dd>
        </div>
      </dl>
    </div>
  );
}

/* O mecanismo do bloco 01, no mesmo formato do das escalas: o que o sistema
   aponta sozinho, sem que alguém precise lembrar de olhar. É o argumento que
   separa "cadastro de membros" (todo sistema tem) de "ninguém escapa pelo
   vão". Cada sinal existe no produto:
    - o plano de acompanhamento do visitante, com data e responsável por
      contato, e o filtro de atrasados (lib/visitor-followup.ts);
    - as faixas de 30/60/90 dias do relatório de esfriamento, lidas da
      presença em culto, chamada e escala (relatorios/esfriamento);
    - o botão de WhatsApp na linha da pessoa, no esfriamento e nos
      aniversariantes.
   Não acrescente sinal aqui sem que a tela correspondente exista. */
const signals = [
  {
    icon: UserPlus,
    label: "O visitante com contato atrasado",
    desc: "Cada visitante ganha um plano de acompanhamento, com data e responsável por contato. Passou do prazo, ele aparece na lista de atrasados.",
  },
  {
    icon: CalendarX2,
    label: "O membro que parou de aparecer",
    desc: "A presença no culto, na célula e na escala vira uma faixa: 30, 60 ou 90 dias sem registro. Ninguém some sem que alguém veja.",
  },
  {
    icon: MessageCircle,
    label: "E o contato a um toque",
    desc: "Da lista, o líder abre o WhatsApp da pessoa com a mensagem começada.",
  },
];

function PessoasMechanism() {
  return (
    <div className="mt-8">
      <p className="text-sm font-semibold text-foreground">
        O sistema aponta quem está sumindo, antes que se perca:
      </p>
      <ul className="mt-4 flex flex-col gap-4">
        {signals.map((signal) => (
          <li key={signal.label} className="flex gap-3">
            <signal.icon
              className="mt-0.5 size-4 shrink-0 text-primary"
              strokeWidth={2}
              aria-hidden="true"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">
                {signal.label}.
              </span>{" "}
              {signal.desc}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

type Highlight = {
  id: string;
  number: string;
  title: string;
  desc: string;
  modules: string[];
  screen: ReactNode;
  extra?: ReactNode;
};

const HIGHLIGHTS: Highlight[] = [
  {
    id: "pessoas",
    number: "01",
    title: "Ninguém escapa pelo vão",
    desc: "O visitante de domingo, o membro que sumiu há três semanas, a criança na sala do infantil. Todo mundo tem ficha, histórico e alguém responsável por acompanhar.",
    modules: ["Membros", "Visitantes", "Células", "Kids"],
    screen: <PessoasScreen />,
    extra: <PessoasMechanism />,
  },
  {
    id: "escalas",
    number: "02",
    title: "A escala do mês em 15 minutos",
    desc: "O mês inteiro publicado, cada voluntário no dia que combina com a disponibilidade que ele mesmo declarou, e o repertório do culto amarrado junto. A escala cai no Google Agenda de cada um.",
    modules: ["Escalas", "Repertório", "Agenda"],
    screen: <EscalasScreen />,
    extra: <EscalasMechanism />,
  },
  {
    id: "financas",
    number: "03",
    title: "Fechar o mês sem planilha",
    desc: "Dízimos, ofertas, contas a pagar e cantina numa tela só, com cada entrada ligada a quem pagou e quando. No fim do mês o relatório da diretoria sai em PDF e a exportação vai pronta para o contador.",
    modules: ["Financeiro", "Relatórios", "Cantina"],
    screen: <FinancasScreen />,
  },
];

export default function Features() {
  const { trackFeaturesFeatureClicked, trackNavClicked } = useDataFastGoals();
  const sectionRef = useSectionViewGoal("features");

  return (
    <section
      ref={sectionRef}
      id="recursos"
      aria-labelledby="recursos-titulo"
      className={SECTION_Y}
    >
      <div className={CONTAINER}>
        <SectionHeading
          id="recursos-titulo"
          title="Três coisas param de dar trabalho na primeira semana."
          muted="O resto dos mais de 20 módulos vem junto."
        >
          São as telas do Ecclesiato como a sua secretaria vai ver. Sem curso,
          sem manual e sem módulo cobrado à parte.
        </SectionHeading>

        <div className="mt-16 flex flex-col gap-20 md:mt-20 md:gap-28">
          {HIGHLIGHTS.map((item, i) => (
            <article
              key={item.id}
              aria-labelledby={`recurso-${item.id}`}
              // Só rastreamento: o clique em qualquer ponto do bloco conta
              // interesse naquele módulo. Não é controle, não recebe foco.
              onClick={() => trackFeaturesFeatureClicked(item.id)}
              className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-center md:gap-12"
            >
              {/* A tela fica numa bancada: fundo neutro em volta, para ela ler
                  como produto em cima da mesa e não como mais um cartão. */}
              <figure
                className={cn(
                  "lp-reveal rounded-[calc(var(--radius)+12px)] bg-secondary/60 p-3 md:col-span-7 md:p-5",
                  i % 2 === 1 && "md:order-2",
                )}
              >
                {/* Altura fixa e igual nas três: telas de alturas diferentes
                    fazem a seção parecer desalinhada. Medida no navegador com
                    as fontes carregadas; a moldura tem overflow-hidden e corta
                    sem avisar, então ao mexer no conteúdo das telas, remeça. */}
                <div className="lp-surface h-124 overflow-hidden md:h-118">
                  {item.screen}
                </div>
                <figcaption className="sr-only">
                  Tela de {item.modules.join(", ")} no Ecclesiato
                </figcaption>
              </figure>

              <div
                className={cn(
                  "lp-reveal md:col-span-5",
                  i % 2 === 1 && "md:order-1",
                )}
              >
                <p className="text-sm font-medium text-muted-foreground tabular-nums">
                  {item.number}
                  <span className="mx-2 text-border" aria-hidden="true">
                    /
                  </span>
                  {item.modules.join(" · ")}
                </p>
                <h3
                  id={`recurso-${item.id}`}
                  className="mt-3 text-2xl font-semibold tracking-tight text-balance text-foreground md:text-3xl"
                >
                  {item.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-pretty text-muted-foreground">
                  {item.desc}
                </p>
                {item.extra}
              </div>
            </article>
          ))}
        </div>

        {/* ── Os outros módulos + CTA ───────────────────────────────────── */}
        {/* A contagem defende a mensalidade em uma linha; quem quer módulo por
            módulo tem /features. O CTA carrega a objeção DESTE ponto: quem
            acabou de ver mais de vinte módulos teme que a conta cresça com eles. */}
        <div className="lp-reveal lp-surface mt-20 flex flex-col gap-6 p-6 md:mt-28 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="max-w-xl">
            <h3 className="text-xl font-semibold tracking-tight text-foreground">
              E mais de dez outros módulos vêm junto
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Eventos, batismos, cuidado pastoral, votações, treinamentos. O
              que muda entre os planos é o limite de pessoas, nunca o que você
              pode usar. Nenhum módulo é cobrado à parte. E os 7 primeiros
              dias são grátis; só cinco funções, como o pagamento online de
              inscrições, liberam na primeira cobrança.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/features"
              onClick={() => trackNavClicked("features_to_catalogo")}
              className={buttonClass("secondary")}
            >
              Ver todos os módulos
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
            <a
              href="#precos"
              onClick={(e) => {
                e.preventDefault();
                trackNavClicked("features_cta");
                scrollToSection("precos");
              }}
              className={buttonClass("primary")}
            >
              Ver os planos
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
